import React, { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { CheckCircle2, AlertCircle, Info, X } from "lucide-react";

type ToastVariant = "success" | "error" | "warning" | "info";

interface SnackbarMessage {
  id: string;
  title: string;
  variant: ToastVariant;
  duration?: number;
}

interface SnackbarContextType {
  showSnackbar: (message: string | unknown, variant: ToastVariant) => void;
}

const PRESTO = {
  blue: "#0079C2",
  green: "#018752",
  text: "#515151",
  border: "#D9D9D9",
  red: "#930000",
};

function getToastStyle(variant: ToastVariant) {
  switch (variant) {
    case "success":
      return {
        icon: CheckCircle2,
        accent: PRESTO.green,
        softBg: "rgba(1, 135, 82, 0.08)",
        label: "Success",
      };
    case "error":
      return {
        icon: AlertCircle,
        accent: PRESTO.red,
        softBg: "rgba(147, 0, 0, 0.08)",
        label: "Failed",
      };
    default:
      return {
        icon: Info,
        accent: PRESTO.blue,
        softBg: "rgba(0, 121, 194, 0.08)",
        label: "Notice",
      };
  }
}

function ToastCard({
  toast,
  onClose,
}: {
  toast: SnackbarMessage;
  onClose: (id: string) => void;
}) {
  const style = getToastStyle(toast.variant);
  const Icon = style.icon;

  useEffect(() => {
    const t = window.setTimeout(() => onClose(toast.id), toast.duration ?? 4000);
    return () => window.clearTimeout(t);
  }, [toast.id, toast.duration, onClose]);

  return (
    <div
      className="relative w-[380px] overflow-hidden rounded-2xl border bg-white shadow-[0_12px_30px_rgba(0,0,0,0.12)]"
      style={{ borderColor: PRESTO.border }}
    >
      <div
        className="absolute left-0 top-0 h-full w-1.5"
        style={{ backgroundColor: style.accent }}
      />

      <div className="flex items-start gap-3 p-3 pl-4">
        <div
          className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full"
          style={{ backgroundColor: style.softBg }}
        >
          <Icon size={18} style={{ color: style.accent }} />
        </div>

        <div className="min-w-0 flex-1">
          <div className="mb-1 flex items-center gap-2">
            <span
              className="rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase tracking-[0.08em]"
              style={{ color: style.accent, backgroundColor: style.softBg }}
            >
              {style.label}
            </span>
          </div>
          <p className="truncate text-sm font-semibold" style={{ color: PRESTO.text }}>
            {toast.title}
          </p>
        </div>

        <button
          onClick={() => onClose(toast.id)}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl transition hover:bg-black/5"
          aria-label="Close notification"
        >
          <X size={16} style={{ color: "rgba(81,81,81,0.75)" }} />
        </button>
      </div>
    </div>
  );
}

const SnackbarContext = createContext<SnackbarContextType | undefined>(undefined);

const SnackbarProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<SnackbarMessage[]>([]);

  const removeToast = (id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  };

  const showSnackbar = (message: string | unknown, variant: ToastVariant) => {
    let title: string;
    if (typeof message === "string") {
      title = message;
    } else if (message && typeof message === "object") {
      const err = message as { message?: string; error?: string; [key: string]: unknown };
      title = err.message || err.error || JSON.stringify(message);
    } else {
      title = String(message || "An error occurred");
    }

    const id = crypto.randomUUID();
    setToasts((prev) => [...prev, { id, title, variant }]);
  };

  return (
    <SnackbarContext.Provider value={{ showSnackbar }}>
      {children}

      <div className="pointer-events-none fixed right-5 top-5 z-[9999999] flex flex-col gap-3">
        {toasts.map((toast) => (
          <div key={toast.id} className="pointer-events-auto animate-[toastIn_.22s_ease-out]">
            <ToastCard toast={toast} onClose={removeToast} />
          </div>
        ))}
      </div>

      <style>{`
        @keyframes toastIn {
          from { opacity: 0; transform: translateY(-10px) scale(0.98); }
          to   { opacity: 1; transform: translateY(0)   scale(1);    }
        }
      `}</style>
    </SnackbarContext.Provider>
  );
};

const useSnackbarContext = (): SnackbarContextType => {
  const context = useContext(SnackbarContext);
  if (!context) {
    throw new Error("useSnackbarContext must be used within a SnackbarProvider");
  }
  return context;
};

export { SnackbarProvider, useSnackbarContext };
